import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { CapacityRunwayView, CounterfactualView } from "../lib/api/types";

const AXIS = { fill: "hsl(var(--muted-foreground))", fontSize: 11 };
const TOOLTIP = {
  background: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "0px",
  color: "hsl(var(--popover-foreground))",
  fontSize: 12,
};

/**
 * Backward-looking: what actually happened vs the pre-Athena trend extrapolated
 * forward. The accent fill marks the attributable gap, post-go-live only.
 */
export function CounterfactualChart({ view }: { view: CounterfactualView }) {
  const data = view.points.map((p) => ({
    period: p.period,
    actualHours: p.actualHours,
    extrapolatedBaselineHours: p.extrapolatedBaselineHours,
    gapBase: p.actualHours == null ? null : p.actualHours,
    gap: p.actualHours == null ? null : Math.max(0, p.extrapolatedBaselineHours - p.actualHours),
  }));

  return (
    <div className="h-[320px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="period" tick={AXIS} stroke="hsl(var(--border))" interval={2} />
          <YAxis
            tick={AXIS}
            stroke="hsl(var(--border))"
            unit="h"
            domain={[0, "auto"]}
            tickFormatter={(v: number) => v.toFixed(0)}
          />
          <Tooltip contentStyle={TOOLTIP} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Area
            dataKey="gapBase"
            stackId="gap"
            stroke="none"
            fill="transparent"
            connectNulls={false}
            isAnimationActive={false}
            legendType="none"
            name="Actual (base)"
          />
          <Area
            dataKey="gap"
            stackId="gap"
            stroke="none"
            fill="var(--amber)"
            fillOpacity={0.16}
            connectNulls={false}
            isAnimationActive={false}
            legendType="none"
            name="Attributable gap"
          />
          <Line
            dataKey="extrapolatedBaselineHours"
            name="Extrapolated baseline — no Athena"
            stroke="var(--neutral-inactive)"
            strokeWidth={2}
            strokeDasharray="6 4"
            dot={false}
            isAnimationActive={false}
          />
          <Line
            dataKey="actualHours"
            name="Actual"
            stroke="var(--amber)"
            strokeWidth={2.4}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

const SCENARIO_COLORS: Record<string, string> = {
  current_trend_continues: "hsl(var(--chart-1))",
  efficiency_plateaus: "hsl(var(--chart-3))",
  volume_grows_20pct: "hsl(var(--chart-2))",
};

/** Forward-looking: every line is dashed because every line is a projection. */
export function CapacityRunwayChart({ view }: { view: CapacityRunwayView }) {
  /**
   * Every line here is a projection of capacityRatio (decisions ÷ active analysts), and no table
   * in athena_ue_performance records analyst headcount — so the backend sends no scenarios and a
   * null ceiling until team_headcount is populated.
   *
   * Rendering the chart anyway would draw an empty axis with a ceiling line at y=0, which reads
   * as "you are already at the ceiling" rather than "this is not measurable yet".
   */
  if (view.scenarios.length === 0 || view.ceilingCapacityRatio == null) {
    return (
      <div className="rounded-lg border border-neutral-inactive/40 bg-neutral-inactive-dim p-4">
        <p className="text-base font-medium text-neutral-inactive">Not yet measurable</p>
        <p className="mt-2 text-xs leading-relaxed text-neutral-inactive">
          {view.ceilingBasisNote}
        </p>
      </div>
    );
  }

  const periods = view.scenarios[0]?.projectedCapacityRatio.map((p) => p.period) ?? [];
  const data = periods.map((period, i) => {
    const row: Record<string, string | number> = { period };
    for (const scenario of view.scenarios) {
      row[scenario.name] = scenario.projectedCapacityRatio[i]?.value ?? 0;
    }
    return row;
  });

  return (
    <div className="h-[320px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 20, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="period" tick={AXIS} stroke="hsl(var(--border))" interval={2} />
          <YAxis
            tick={AXIS}
            stroke="hsl(var(--border))"
            domain={["auto", "auto"]}
            tickFormatter={(v: number) => v.toFixed(0)}
          />
          <Tooltip contentStyle={TOOLTIP} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <ReferenceLine
            y={view.ceilingCapacityRatio}
            stroke="hsl(var(--foreground))"
            strokeDasharray="5 5"
            label={{
              value: "Ceiling — highest sustained rate observed",
              position: "insideTopLeft",
              fill: "hsl(var(--foreground))",
              fontSize: 11,
            }}
          />
          {view.scenarios.map((scenario) => (
            <Line
              key={scenario.name}
              dataKey={scenario.name}
              name={scenario.label}
              stroke={SCENARIO_COLORS[scenario.name] ?? "hsl(var(--chart-5))"}
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
